import React, {useEffect, useMemo, useState} from 'react';
import {classes, style} from './doppe-action-type-twitter-embed-widget.st.css';
import {AsyncValueStates, ButtonType, useAsyncState, useRootWindow} from '@wix/devzai-utils-react';
import {
    DoppeActionType,
    useIsWidgetInFixedHeight
} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {DoppeActionTypeTwitterEmbed} from '../doppe-action-type-twitter-embed';
import {domLoadScript} from '@wix/devzai-utils-dom';
import {
    DoppePageAsyncValueRenderer
} from '../../../components/doppe-page-async-value-renderer/doppe-page-async-value-renderer';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import {
    DoppeActionTypeWidgetInnerButton
} from '../../../components/doppe-action-type-widget-inner-button/doppe-action-type-widget-inner-button';
import {asyncDelay, doppeValidateTweetURL} from '@wix/devzai-utils-common';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapper
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper/doppe-action-type-widget-content-layout-new-wrapper';
import {useDoppeViewerLocales} from "../../../client/doppe-viewer-locales/doppe-viewer-locales";

export const DoppeActionTypeTwitterEmbedWidget = React.memo(function DoppeActionTypeSubscribeEmbedWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeTwitterEmbed.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const actionSettings = actionContext.action.settings;

    const twitterUrl = actionSettings.twitterUrl

    const rootWindow = useRootWindow();

    const [loadTwitterState, setLoadTwitterState] = useAsyncState(false);

    useEffect(() => {
        setLoadTwitterState(
            domLoadScript('https://platform.twitter.com/widgets.js', {
                rootWindow: rootWindow,
                skipIfAlreadyLoaded: false
            }).then(async () => {
                return asyncDelay(1000).then(() => {
                    return true;
                })
            })
        );
    }, [rootWindow, actionSettings.theme, actionSettings.twitterUrl]);

    const isTweetUrl = doppeValidateTweetURL(twitterUrl);

    const isWidgetInFixedHeight = useIsWidgetInFixedHeight();

    const [expanded, setExpanded] = useState(isTweetUrl || isWidgetInFixedHeight);

    const doppeViewerLocales = useDoppeViewerLocales();

    const normalizedTwitterUrl = useMemo(()=>{
        return convertToTwitterLink(twitterUrl);
    }, [twitterUrl]);


    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {
                expanded: expanded,
                isFeed: !isTweetUrl,
                isWidgetInFixedHeight: isWidgetInFixedHeight
            }, className)}
            actionContext={actionContext}
        >
            <DoppeActionTypeWidgetContentLayoutNewWrapper
                fullWidthMainMedia={false}
                footer={!isTweetUrl && !isWidgetInFixedHeight && loadTwitterState.state === AsyncValueStates.Resolved ? (
                    <div
                        className={style(classes.footerContainer, {}, FlexLayout.column.alignToCenter)}
                    >
                        <DoppeActionTypeWidgetInnerButton
                            className={style(classes.linkButton)}
                            buttonType={ButtonType.NormalButton}
                            onClick={() => {
                                setExpanded(!expanded);
                                actionContext.reportGoalAchieved()
                            }}
                        >
                            {expanded ? doppeViewerLocales['viewLessButton'] : doppeViewerLocales['viewMoreButton']}
                        </DoppeActionTypeWidgetInnerButton>
                    </div>
                ) : undefined}
            >
                <div
                    key={normalizedTwitterUrl + actionSettings.theme}
                    className={style(classes.content, {}, FlexLayout.column.alignToCenter, isWidgetInFixedHeight ? FlexLayout.fillRemainingVerticalSpace : undefined)}
                >
                    {
                        isTweetUrl ?
                            <blockquote
                                className={style(classes.innerContent, {}, 'twitter-tweet' )}
                                data-theme={actionSettings.theme}
                            >
                                <a href={normalizedTwitterUrl}
                                />
                            </blockquote>
                            :
                            <a
                                className={style(classes.innerContent, {}, 'twitter-timeline')}
                                href={normalizedTwitterUrl}
                                data-theme={actionSettings.theme}
                            />
                    }
                    <DoppePageAsyncValueRenderer
                        asyncValue={loadTwitterState}
                        className={style(classes.loaderContent, {})}
                    >
                        {
                            () => {
                                return (
                                    <div
                                        className={style(classes.loadedContent, {}, CommonClasses.button, isWidgetInFixedHeight ? FlexLayout.fillRemainingVerticalSpace : undefined)}
                                        onClick={() => {
                                            actionContext.reportGoalAchieved();
                                            actionContext.navigateToUrl(normalizedTwitterUrl, true);
                                        }}
                                    />
                                )
                            }
                        }
                    </DoppePageAsyncValueRenderer>
                </div>
            </DoppeActionTypeWidgetContentLayoutNewWrapper>
        </DoppeActionTypeWidgetLayout>
    );
});

function convertToTwitterLink(originalLink: string) {
    return originalLink.replace("x.com/", "twitter.com/");
}
